import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  fetchAssetsTypesList,
  setDestroy,
} from "containers/assets-types/assets-types-container.slice";
import {
  createAssetsTypes,
  updateAssetsTypes,
} from "services/assets-types.service";
import {
  CatalogTable,
  ChangeLink,
  EditPanel,
  SettingsCard,
  useSaveWithToast,
  useScrollIntoViewOnOpen,
} from "containers/settings/ui";
import {
  AddButton,
  Field,
  FIELD_INPUT_CLASS,
  PanelHeading,
} from "containers/settings/catalog-form-parts";

type AssetsTypeDraft = { id?: string; title: string };

const NEW_ASSETS_TYPE: AssetsTypeDraft = { title: "" };

type AssetsTypeEditPanelProps = {
  saved: AssetsTypeDraft;
  companyId: string;
  onSaved: () => void;
  onCancel: () => void;
};

function AssetsTypeEditPanel({
  saved,
  companyId,
  onSaved,
  onCancel,
}: AssetsTypeEditPanelProps) {
  const { t } = useTranslation();
  const { save, saving, error } = useSaveWithToast();
  const [draft, setDraft] = useState(saved);
  const [triedToSave, setTriedToSave] = useState(false);

  const title = draft.title.trim();

  const submit = async () => {
    setTriedToSave(true);
    if (!title) return;

    const payload = { id: draft.id, title };
    const wasSaved = await save(() =>
      draft.id
        ? updateAssetsTypes(payload, companyId)
        : createAssetsTypes(payload, companyId)
    );
    if (wasSaved) onSaved();
  };

  return (
    <EditPanel
      onSave={submit}
      onCancel={onCancel}
      saving={saving}
      error={triedToSave && !title ? t("requiredFields") : error}
    >
      <PanelHeading>
        {saved.id
          ? `${t("settings_change")}: ${saved.title}`
          : t("assets_types_add")}
      </PanelHeading>
      <Field htmlFor="assets-type-title" label={t("title")}>
        <input
          id="assets-type-title"
          className={FIELD_INPUT_CLASS}
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          autoComplete="off"
          autoFocus
        />
      </Field>
    </EditPanel>
  );
}

function AssetsTypesContainer() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const { collection, isLoading, isError } = useAppSelector(
    (state) => state.assetsTypesReducer
  );
  const [editing, setEditing] = useState<AssetsTypeDraft | null>(null);
  const panelRef = useScrollIntoViewOnOpen(editing);

  useEffect(() => {
    if (companyId) dispatch(fetchAssetsTypesList(companyId));
    return () => {
      dispatch(setDestroy());
    };
  }, [companyId, dispatch]);

  if (!companyId) return null;

  const startEdit = (id: string) => {
    const row = collection.find((assetsType) => assetsType.id === id);
    if (row) setEditing({ id: row.id, title: row.title });
  };

  const closeAndReload = () => {
    setEditing(null);
    dispatch(fetchAssetsTypesList(companyId));
  };

  return (
    <SettingsCard
      title={t("settings_nav_asset_types_label")}
      actions={
        <AddButton
          label={t("assets_types_add")}
          onClick={() => setEditing(NEW_ASSETS_TYPE)}
        />
      }
    >
      {editing && (
        <div ref={panelRef} className="mb-4">
          <AssetsTypeEditPanel
            key={editing.id ?? "new"}
            saved={editing}
            companyId={companyId}
            onSaved={closeAndReload}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}
      <CatalogTable
        columns={[{ key: "title", header: t("assets_types_type") }]}
        rows={collection.map((assetsType) => ({
          id: assetsType.id,
          cells: { title: assetsType.title },
        }))}
        renderRowAction={(id) => <ChangeLink onClick={() => startEdit(id)} />}
        empty={t("assets_types_empty")}
        isLoading={isLoading}
        loadError={isError ? t("settings_load_failed") : undefined}
      />
      <p className="mt-3 text-sm text-gray-600">
        {t("assets_types_requests_note")}
      </p>
    </SettingsCard>
  );
}

export default AssetsTypesContainer;
